import React, { useState, useCallback } from 'react';
import { View, Text, Image, TouchableOpacity, ActivityIndicator } from 'react-native';
import { Editor } from 'primereact/editor';
import AsyncStorage from '@react-native-async-storage/async-storage';
import LabeledTextInput from '../../../../common/components/bohform/LabeledTextInput';
import { MultiSelect } from 'react-native-element-dropdown';
import Checkbox from 'expo-checkbox';
import Select from '../../../../common/components/ui/Select';
import { API_URL } from '../../../../common/constants/AppConstants';

export type FieldType =
  | 'text'
  | 'textarea'
  | 'html'
  | 'image'
  | 'link'
  | 'json'
  | 'color'
  | 'multiselect'
  | 'select'
  | 'checkbox';

export interface Setting {
  id: number;
  key_name: string;
  label: string;
  description: string | null;
  field_type: FieldType;
  value: string;
  lang: string;
  group_name: string | null;
  sort_order: number;
  is_active: 0 | 1;
}

interface BrandContentFieldProps {
  setting: Setting;
  brandId?: number;
  onValueChange?: (id: number, newValue: string) => void;
  customStyle?: {
    container?: any;
    label?: any;
    editor?: any;
    input?: any;
  };
  options?: { label: string; value: string }[];
}

const resolveImageUrl = (value: string) =>
  !value ? '' : value.startsWith('http') ? value : `${API_URL}${value}`;

const BrandContentField: React.FC<BrandContentFieldProps> = React.memo(
  ({ setting, brandId, onValueChange, customStyle = {}, options }) => {
    const [localValue, setLocalValue] = useState(setting.value || '');
    const [isUploading, setIsUploading] = useState(false);

    const handleChange = useCallback(
      (newValue: any) => {
        const formatted =
          setting.field_type === 'multiselect' ? JSON.stringify(newValue) : newValue;
        setLocalValue(formatted);
        onValueChange?.(setting.id, formatted);
      },
      [onValueChange, setting.id, setting.field_type]
    );

    const handleImageUpload = useCallback(
      async (file: File) => {
        if (!file) return;
        setIsUploading(true);
        try {
          const token = await AsyncStorage.getItem('access-token');
          const formData = new FormData();
          formData.append('brand_id', String(brandId ?? ''));
          formData.append('image', file);
          const response = await fetch(`${API_URL}/settings/uploadcontentimage`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
            body: formData,
          });
          const jsonRes = await response.json();
          if (jsonRes?.success && jsonRes.url) {
            setLocalValue(jsonRes.url);
            onValueChange?.(setting.id, jsonRes.url);
          } else {
            window.alert(jsonRes?.message || 'Failed to upload image');
          }
        } catch (e) {
          window.alert('Failed to upload image');
        } finally {
          setIsUploading(false);
        }
      },
      [brandId, onValueChange, setting.id]
    );

    const quillModules = {
      clipboard: {
        matchVisual: false,
      },
    };

    switch (setting.field_type) {
      case 'text':
        return (
          <LabeledTextInput
            containerStyle={customStyle.container}
            label={setting.label}
            defaultValue={localValue}
            onChangeText={handleChange}
            inputStyle={customStyle.input}
            labelStyle={customStyle.label}
          />
        );

      case 'link':
        return (
          <LabeledTextInput
            containerStyle={customStyle.container}
            label={setting.label}
            defaultValue={localValue}
            onChangeText={handleChange}
            inputStyle={customStyle.input}
            labelStyle={customStyle.label}
            placeholder="https://example.com"
            keyboardType="url"
            autoCapitalize="none"
          />
        );

      case 'textarea':
        return (
          <LabeledTextInput
            containerStyle={customStyle.container}
            label={setting.label}
            defaultValue={localValue}
            onChangeText={handleChange}
            inputStyle={[{ height: 120, textAlignVertical: 'top', paddingTop: 8 }, customStyle.input]}
            labelStyle={customStyle.label}
            multiline
            numberOfLines={4}
          />
        );

      case 'json':
        return (
          <LabeledTextInput
            containerStyle={customStyle.container}
            label={setting.label}
            defaultValue={localValue}
            onChangeText={handleChange}
            inputStyle={[
              { height: 160, textAlignVertical: 'top', paddingTop: 8, fontFamily: 'monospace' },
              customStyle.input,
            ]}
            labelStyle={customStyle.label}
            placeholder='{ "key": "value" }'
            multiline
            numberOfLines={6}
            autoCapitalize="none"
            autoCorrect={false}
          />
        );

      case 'color': {
        const localOptions = options ?? [];
        return (
          <Select
            label={setting.label}
            options={localOptions}
            value={localValue}
            onChange={(newValue) => {
              setLocalValue(newValue);
              onValueChange?.(setting.id, newValue);
            }}
            customStyle={customStyle}
            placeholder="Select color"
            renderLeftIcon={() =>
              localValue ? (
                <View
                  style={{
                    width: 16,
                    height: 16,
                    borderRadius: 2,
                    borderWidth: 1,
                    borderColor: '#ccc',
                    backgroundColor: localValue,
                    marginRight: 8,
                  }}
                />
              ) : null
            }
            renderItem={(item: any) => (
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  paddingVertical: 4,
                  paddingHorizontal: 8,
                }}>
                <View
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: 2,
                    borderWidth: 1,
                    borderColor: '#ccc',
                    backgroundColor: item.value,
                    marginRight: 8,
                  }}
                />
                <Text>{item.label}</Text>
              </View>
            )}
          />
        );
      }

      case 'select': {
        const localOptions = options ?? [];
        return (
          <Select
            label={setting.label}
            options={localOptions}
            value={localValue}
            onChange={(newValue) => {
              setLocalValue(newValue);
              onValueChange?.(setting.id, newValue);
            }}
            customStyle={customStyle}
            placeholder="Select"
          />
        );
      }

      case 'html':
        return (
          <View style={customStyle.container}>
            <Text style={[{ fontWeight: '600', marginBottom: 4 }, customStyle.label]}>
              {setting.label}
            </Text>
            <Editor
              value={localValue}
              onTextChange={(e) => handleChange(e.htmlValue)}
              style={{ height: 200, ...(customStyle.editor || {}) }}
              modules={quillModules}
            />
          </View>
        );

      case 'multiselect':
        // Generate 30-min interval times for full 24 hours in 12-hour format
        const generateTimeSlots = () => {
          const slots: { label: string; value: string }[] = [];
          let start = new Date();
          start.setHours(0, 0, 0, 0); // 12:00 AM (start of day)
          
          // Generate 48 slots (24 hours * 2 slots per hour)
          for (let i = 0; i < 48; i++) {
            const hours = start.getHours();
            const minutes = start.getMinutes();
            const suffix = hours >= 12 ? 'PM' : 'AM';
            const displayHours = hours % 12 || 12;
            const displayMinutes = minutes === 0 ? '00' : minutes.toString().padStart(2, '0');
            const label = `${displayHours}:${displayMinutes} ${suffix}`;
            slots.push({ label, value: label });
            start.setMinutes(start.getMinutes() + 30);
          }
          return slots;
        };

        const timeSlots = generateTimeSlots();

        // Parse comma-separated string to array and sort chronologically
        const parseTimeToMinutes = (timeStr: string) => {
          const match = timeStr.match(/(\d+):(\d+)\s*(AM|PM)/);
          if (!match) return 0;
          let hours = parseInt(match[1]);
          const minutes = parseInt(match[2]);
          const suffix = match[3];
          if (suffix === 'AM' && hours === 12) hours = 0;
          if (suffix === 'PM' && hours !== 12) hours += 12;
          return hours * 60 + minutes;
        };
        const selectedValues = localValue?.length > 0
          ? localValue.split(',').sort((a, b) => parseTimeToMinutes(a) - parseTimeToMinutes(b))
          : [];

        const amSelected = selectedValues.filter(v => v.includes('AM'));
        const pmSelected = selectedValues.filter(v => v.includes('PM'));

        const removeSlot = (val: string) => {
          const updated = selectedValues.filter(v => v !== val);
          const newValue = updated.join(',');
          setLocalValue(newValue);
          onValueChange?.(setting.id, newValue);
        };

        return (
          <View style={customStyle.container}>
            <Text style={customStyle.label}>{setting.label}</Text>
            <MultiSelect
              style={[
                {
                  borderWidth: 1,
                  borderColor: '#808080',
                  borderRadius: 6,
                  padding: 8,
                  minHeight: 40,
                },
                customStyle.input,
              ]}
              data={timeSlots}
              labelField="label"
              valueField="value"
              placeholder="Select time slots"
              search={false}
              value={selectedValues}
              onChange={(selectedArray: string[]) => {
                const newValue = selectedArray.join(',');
                setLocalValue(newValue);
                onValueChange?.(setting.id, newValue);
              }}
              renderItem={(item: any) => {
                const isSelected = selectedValues.includes(item.value);
                const isAM = item.value.includes('AM');
                return (
                  <View
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      paddingVertical: 8,
                      paddingHorizontal: 12,
                      backgroundColor: isSelected ? (isAM ? '#e8f5e9' : '#e3f2fd') : 'transparent',
                    }}
                  >
                    <Checkbox
                      value={isSelected}
                      onValueChange={() => {}}
                      style={{ marginRight: 8 }}
                      color={isSelected ? (isAM ? '#43a047' : '#1e88e5') : undefined}
                    />
                    <Text style={{ fontSize: 14, color: isSelected ? (isAM ? '#2e7d32' : '#1565c0') : '#333', fontWeight: isSelected ? '600' : '400' }}>
                      {item.label}
                    </Text>
                  </View>
                );
              }}
              renderSelectedItem={() => <View />}
            />

            {selectedValues.length > 0 && (
              <View style={{ marginTop: 8, borderWidth: 1, borderColor: '#e0e0e0', borderRadius: 8, padding: 10 }}>
                {/* Summary badges */}
                <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8, gap: 8 }}>
                  <Text style={{ fontSize: 12, color: '#555', marginRight: 4 }}>Selected:</Text>
                  {amSelected.length > 0 && (
                    <View style={{ backgroundColor: '#e8f5e9', borderRadius: 12, paddingHorizontal: 10, paddingVertical: 3, borderWidth: 1, borderColor: '#a5d6a7' }}>
                      <Text style={{ fontSize: 12, color: '#2e7d32', fontWeight: '600' }}>🌅 AM · {amSelected.length}</Text>
                    </View>
                  )}
                  {pmSelected.length > 0 && (
                    <View style={{ backgroundColor: '#e3f2fd', borderRadius: 12, paddingHorizontal: 10, paddingVertical: 3, borderWidth: 1, borderColor: '#90caf9' }}>
                      <Text style={{ fontSize: 12, color: '#1565c0', fontWeight: '600' }}>🌇 PM · {pmSelected.length}</Text>
                    </View>
                  )}
                  <Text
                    onPress={() => { setLocalValue(''); onValueChange?.(setting.id, ''); }}
                    style={{ marginLeft: 'auto', fontSize: 12, color: '#e53935', fontWeight: '600' }}
                  >
                    Clear all
                  </Text>
                </View>
                {/* Chips in a scrollable compact row */}
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 5 }}>
                  {selectedValues.map((val) => {
                    const isAM = val.includes('AM');
                    return (
                      <View key={val} style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: 110,
                        backgroundColor: isAM ? '#f1f8e9' : '#e8f4fd',
                        borderWidth: 1,
                        borderColor: isAM ? '#c5e1a5' : '#b3d9f8',
                        borderRadius: 16,
                        paddingHorizontal: 8,
                        paddingVertical: 3,
                      }}>
                        <Text style={{ fontSize: 11, color: isAM ? '#33691e' : '#0d47a1', fontWeight: '500' }}>{val}</Text>
                        <Text onPress={() => removeSlot(val)} style={{ fontSize: 14, color: isAM ? '#558b2f' : '#1565c0', fontWeight: 'bold', lineHeight: 16 }}>×</Text>
                      </View>
                    );
                  })}
                </View>
              </View>
            )}
          </View>
        );

      case 'image': {
        const displayUrl = resolveImageUrl(localValue);
        return (
          <View style={customStyle?.container}>
            <Text style={[{ fontWeight: '600', marginBottom: 6 }, customStyle?.label]}>
              {setting.label}
            </Text>
            {displayUrl ? (
              <Image
                source={{ uri: displayUrl }}
                style={{ width: 160, height: 100, borderRadius: 6, marginBottom: 8, backgroundColor: '#f2f2f2' }}
                resizeMode="cover"
              />
            ) : (
              <View style={{ width: 160, height: 100, borderRadius: 6, marginBottom: 8, backgroundColor: '#f2f2f2', alignItems: 'center', justifyContent: 'center' }}>
                <Text style={{ color: '#999', fontSize: 12 }}>No image</Text>
              </View>
            )}
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
              <label
                style={{
                  backgroundColor: '#7c3aed',
                  color: '#fff',
                  fontSize: 13,
                  fontWeight: 600,
                  padding: '8px 12px',
                  borderRadius: 6,
                  cursor: 'pointer',
                }}
              >
                {displayUrl ? 'Replace Image' : 'Upload Image'}
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={(e: any) => {
                    const file = e.target.files?.[0];
                    if (file) handleImageUpload(file);
                    e.target.value = '';
                  }}
                />
              </label>
              {displayUrl ? (
                <TouchableOpacity
                  onPress={() => { setLocalValue(''); onValueChange?.(setting.id, ''); }}
                >
                  <Text style={{ color: '#e53935', fontSize: 13, fontWeight: '600' }}>Remove</Text>
                </TouchableOpacity>
              ) : null}
              {isUploading ? <ActivityIndicator size="small" /> : null}
            </View>
          </View>
        );
      }

      case 'checkbox': {
        const checked = localValue === 'true' || localValue === '1';
        return (
          <View style={customStyle.container}>
            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              <Checkbox
                value={checked}
                onValueChange={(newChecked: boolean) => {
                  const newValue = newChecked ? '1' : '0';
                  setLocalValue(newValue);
                  onValueChange?.(setting.id, newValue);
                }}
              />
              <Text style={[{ marginLeft: 10 }, customStyle.label]}>{setting.label}</Text>
            </View>
          </View>
        );
      }
      default:
        return null;
    }
  },
  (prevProps, nextProps) =>
    prevProps.setting.id === nextProps.setting.id &&
    prevProps.setting.value === nextProps.setting.value
);

export default BrandContentField;
